import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { io, Socket } from 'socket.io-client';
import { environment } from '../../../../../environments/environment';

export type PipelineSocketEvent = 'layer:updated' | 'job:updated' | 'layer-log:updated';

export interface PipelineSocketMessage {
  event: PipelineSocketEvent;
  payload: unknown;
}

/**
 * Serviço de Socket.IO para eventos em tempo real do pipeline (ex.: layer:updated, job:updated).
 * Conecta ao backend na primeira escuta e reutiliza a mesma conexão.
 */
@Injectable({
  providedIn: 'root',
})
export class LayerSocketService {
  private socket: Socket | null = null;

  /**
   * Conecta ao servidor Socket.IO se ainda não estiver conectado.
   * Usa socketUrl do environment (mesmo host da API por padrão).
   */
  private connect(): Socket {
    if (this.socket?.connected) {
      return this.socket;
    }
    const env = environment as { socketUrl?: string; apiUrl: string };
    const url = env.socketUrl ?? env.apiUrl;
    this.socket = io(url, {
      transports: ['websocket', 'polling'],
      reconnection: true,
    });
    return this.socket;
  }

  /**
   * Observable único que emite eventos de 'layer:updated' e 'job:updated'.
   * Uma única conexão e uma única inscrição; no consumidor filtre por `event`.
   */
  onPipelineEvents(): Observable<PipelineSocketMessage> {
    return new Observable((subscriber) => {
      const socket = this.connect();
      const onLayer = (payload: unknown) =>
        subscriber.next({ event: 'layer:updated', payload });
      const onJob = (payload: unknown) =>
        subscriber.next({ event: 'job:updated', payload });
      const onLayerLog = (payload: unknown) =>
        subscriber.next({ event: 'layer-log:updated', payload });
      socket.on('layer:updated', onLayer);
      socket.on('job:updated', onJob);
      socket.on('layer-log:updated', onLayerLog);
      return () => {
        socket.off('layer:updated', onLayer);
        socket.off('job:updated', onJob);
        socket.off('layer-log:updated', onLayerLog);
      };
    });
  }

  /**
   * Desconecta o socket (útil em testes ou ao sair da aplicação).
   */
  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }
}
